// Renders a `table` MessagePart in the chat stream.
// Horizontally scrollable on narrow viewports so the drawer stays readable.
// When the table overflows its container, a small fade + chevron shows on
// the right edge so users know there's more to swipe to. Detected via
// ResizeObserver so it adapts to viewport rotation, drawer resize, and
// the iOS keyboard reflowing the surface.

import { useEffect, useRef, useState } from "react";
import { ChevronRight } from "lucide-react";
import type { MessagePart } from "@/types/consultant";

type TablePart = Extract<MessagePart, { type: "table" }>;

export function InlineTable({
  part,
  surface = "dark",
}: {
  part: TablePart;
  // "dark" = default (MI drawer + Clara user bubbles, dark background).
  // "light" = ivory data surface (Clara assistant bubbles where the
  // table contrasts as a clean cream panel against navy chrome).
  surface?: "dark" | "light";
}) {
  const isLight = surface === "light";
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const measure = () => {
      const overflow = el.scrollWidth > el.clientWidth + 1;
      setHasOverflow(overflow);
      // Hide the affordance once the user has scrolled to (or near) the
      // right edge — they've seen everything, no further hint needed.
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
      setScrolledToEnd(atEnd);
    };

    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(el);
    el.addEventListener("scroll", measure, { passive: true });

    return () => {
      ro.disconnect();
      el.removeEventListener("scroll", measure);
    };
  }, [part]);

  const showAffordance = hasOverflow && !scrolledToEnd;

  // Light theme = inset paler card on the ivory bubble; dark theme =
  // shadcn semantic tokens (unchanged for MI / Clara user bubbles).
  const wrapper = isLight
    ? "rounded-md border border-[#E5E5DF] bg-white overflow-hidden my-2"
    : "rounded-md border border-border bg-background/40 overflow-hidden my-2";
  const titleBar = isLight
    ? "px-3 py-2 border-b border-[#E5E5DF] bg-black/[0.03]"
    : "px-3 py-2 border-b border-border bg-muted/20";
  const titleText = isLight
    ? "text-xs font-semibold uppercase tracking-wider text-[#6A6A6A]"
    : "text-xs font-semibold uppercase tracking-wider text-muted-foreground";
  const headerRow = isLight ? "border-b border-[#E5E5DF]" : "border-b border-border";
  const headerCell = isLight
    ? "px-3 py-2 text-left font-medium text-[#6A6A6A] whitespace-nowrap"
    : "px-3 py-2 text-left font-medium text-muted-foreground whitespace-nowrap";
  const bodyRow = isLight
    ? "border-b border-[#E5E5DF]/70 last:border-b-0 hover:bg-black/[0.02]"
    : "border-b border-border/60 last:border-b-0 hover:bg-muted/10";
  const bodyCell = isLight
    ? "px-3 py-1.5 text-[#1A1A1A] whitespace-nowrap tabular-nums"
    : "px-3 py-1.5 text-foreground whitespace-nowrap tabular-nums";
  const fadeBg = isLight
    ? "pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1 pl-6 bg-gradient-to-l from-white via-white/60 to-transparent"
    : "pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1 pl-6 bg-gradient-to-l from-background/90 via-background/60 to-transparent";
  const fadeChevron = isLight
    ? "h-3.5 w-3.5 text-[#6A6A6A] animate-pulse"
    : "h-3.5 w-3.5 text-muted-foreground animate-pulse";
  const footnoteBar = isLight
    ? "px-3 py-1.5 border-t border-[#E5E5DF] bg-black/[0.02]"
    : "px-3 py-1.5 border-t border-border bg-muted/10";
  const footnoteText = isLight
    ? "text-[10px] text-[#6A6A6A] italic leading-snug"
    : "text-[10px] text-muted-foreground italic leading-snug";

  return (
    <div className={wrapper}>
      <div className={titleBar}>
        <p className={titleText}>{part.title}</p>
      </div>
      <div className="relative">
        <div ref={scrollRef} className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className={headerRow}>
                {part.columns.map((col, i) => (
                  <th key={i} className={headerCell}>
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {part.rows.map((row, rowIdx) => (
                <tr key={rowIdx} className={bodyRow}>
                  {row.map((cell, colIdx) => (
                    <td key={colIdx} className={bodyCell}>
                      {cell == null ? "—" : cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Right-edge fade + chevron, shown only while there's hidden
            content to the right. Pointer-events-none so it never
            blocks the underlying scroll. */}
        {showAffordance && (
          <div aria-hidden="true" className={fadeBg}>
            <ChevronRight className={fadeChevron} />
          </div>
        )}
      </div>
      {part.footnote && (
        <div className={footnoteBar}>
          <p className={footnoteText}>{part.footnote}</p>
        </div>
      )}
    </div>
  );
}
